import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const EMPTY = { code: "", name: "", type: "clinic", address: "" };

export function EditFacilityButton({ facility, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", address: "" });
  const [busy, setBusy] = useState(false);
  const show = () => { setF({ name: facility.name, address: facility.address || "" }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.patch(`/practice/facilities/${facility.code}`, f); toast.success("Facility updated (audited)"); setOpen(false); onSaved(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={show} aria-label={`Edit ${facility.name}`} title="Edit name and address" data-testid={`facility-edit-${facility.code}`}
        className="ml-1 p-1 rounded text-slate-400 hover:text-indigo-700 hover:bg-indigo-50 transition-colors align-middle"><Pencil className="w-3.5 h-3.5" /></button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="facility-edit-dialog">
          <DialogHeader><DialogTitle>Edit facility</DialogTitle><DialogDescription>Code <span className="font-mono">{facility.code}</span> can't change; packets and patients link to it.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Name *</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="facility-edit-name" /></div>
            <div><Label>Address (optional)</Label><Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} placeholder="Street, city, state ZIP" data-testid="facility-edit-address" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-brand" disabled={busy} data-testid="facility-edit-save">{busy ? "Saving..." : "Save"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function AddFacilityButton({ types, onAdded }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const r = await api.post("/practice/facilities", f); toast.success(`Facility ${r.data.code} added (audited)`); setOpen(false); setF(EMPTY); onAdded(); } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button size="sm" className="btn-brand" onClick={() => setOpen(true)} data-testid="facility-add-btn"><Plus className="w-4 h-4 mr-1" />Add facility</Button>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setF(EMPTY); } }}>
        <DialogContent className="max-w-md" data-testid="facility-add-dialog">
          <DialogHeader><DialogTitle>Add facility</DialogTitle><DialogDescription>SSN collection starts off; change it in the list after adding.</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div className="grid grid-cols-[120px_1fr] gap-3">
              <div><Label>Code *</Label><Input value={f.code} onChange={set("code")} placeholder="e.g. AB-WEST" className="uppercase" required data-testid="facility-add-code" /></div>
              <div><Label>Name *</Label><Input value={f.name} onChange={set("name")} placeholder="e.g. Westside Clinic" required data-testid="facility-add-name" /></div>
            </div>
            <div><Label>Type *</Label><Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="facility-add-type"><SelectValue /></SelectTrigger>
              <SelectContent>{types.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Address (optional)</Label><Input value={f.address} onChange={set("address")} placeholder="Street, city, state ZIP" data-testid="facility-add-address" /></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => { setOpen(false); setF(EMPTY); }}>Cancel</Button>
              <Button type="submit" className="btn-brand" disabled={busy} data-testid="facility-add-save">{busy ? "Adding..." : "Add facility"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
